import { useItemImageFailure } from "@/hooks/use-item-image-failure";
import {
  ItemEmptyImageBox,
  ItemImageBox,
  type ItemImageBoxSlot,
} from "@/components/ui/item-image-box";
import type { ItemImageBoxSize } from "@/components/ui/item-image-box-sizes";
import {
  ITEM_PLACEHOLDER_ALT,
  itemImageAlt,
  itemImageSources,
  type CatalogItemImageInput,
} from "@/lib/catalog-item-image";

// Which of the donor's two image boxes a catalog item gets, and what goes in it.
//
// The boxes themselves are shared components (`@/components/ui/item-image-box`)
// because they are shared in the source; this file is the catalog's *choice*
// between them, which is the part that is genuinely per-screen. The four
// outcomes, and the donor line each is:
//
//   1. **A picture** — `ItemImageBox` over the URL set the read contract emits
//      (#297), in a square box with `object-fit: contain`.
//   2. **The house illustration**, for an item with no images. The card and the
//      row substitute `Home_Illustration.svg` as the box's own `imgUrl`
//      (`item-card.tsx:202`, `item-data-row.tsx:186`), so `ItemImageBox`'s
//      "Missing Image" arm is never reached from either.
//   3. **"Missing Image" on a tinted panel**, for an item with no images that is
//      still awaiting approval — `ItemEmptyImageBox`'s `isPendingApproval` arm,
//      which spec #269 (story 2) carries onto the catalog cards.
//   4. **"Missing Image" in words**, bordered — `ItemImageBox`'s own no-URL arm.
//      The table's empty cell, which hands the box an empty URL rather than
//      substituting an illustration (`items-data-table.tsx:120`).
//
// A hero whose fetch *fails* falls back to whichever empty arm applies, never a
// broken-image icon. That arm is #302's by ruling rather than by port: the three
// catalog donors have no `onError` at all, but #302's `## What to build` and
// `## Parity` carry it onto these cards by name, citing spec #269 story 3.
//
// **A modsy user does not see a broken-image icon there either**, contrary to
// what this comment claimed until #496. The donor needs no `onError` because its
// image host answers a missing object with a CloudFront custom error response
// whose *body is a decodable placeholder PNG* — a 403 carrying a 401x400 grey
// glyph — so the `<img>` fires `load`, not `error`. Measured against the running
// stack and recorded on #496. The fallback is host-level and slot-independent
// over there; here it is client-side and per slot, which is why Atlas draws its
// own arms and #302's ruling is what decides which.

export { ITEM_PLACEHOLDER_ILLUSTRATION } from "@/components/ui/item-image-box-sizes";

/** What `ItemHeroImage` reads off an item — a structural subset of the summary. */
interface CatalogItemHeroInput {
  name: string;
  isApproved: boolean;
  heroImage?: CatalogItemImageInput | null;
}

interface ItemImageBaseProps {
  /**
   * The item's hero image, or `null`/absent when it has none.
   *
   * Already resolved server-side — lowest `orderIndex`, tiebreak `id`, emitted
   * hero-first (ADR-0005 decision 9) — so nothing here repeats the donor's
   * `hero_image -> is_hero -> images[0]` chain.
   *
   * That collapse has one **ruled** consequence in the table. The donor's card
   * and row walk the chain, but its table reads `hero_image?.url` with no
   * fallback (`items-data-table.tsx:117-125`) — so a modsy item holding images
   * with no *designated* hero shows a picture on the card and "Missing Image"
   * in the table. Atlas shows the picture in both, because the wire carries no
   * "a hero was designated" fact to be stricter about. Ruled a donor bug not to
   * be ported (#302 decision round, D2): in Atlas the hero is simply the image
   * at the lowest `orderIndex`, in every view. No wire field is coming for it.
   */
  image?: CatalogItemImageInput | null;
  /**
   * The item's name, which is all the alt text needs.
   *
   * Required-shaped rather than optional-with-a-default on purpose: the donor
   * writes a *different* alt for a named and an unnamed item, so a call site
   * that forgets it should be a compile error, not a silently generic label.
   */
  itemName: string | null | undefined;
  size?: ItemImageBoxSize;
  bordered?: boolean;
  /**
   * What stands in when the item has no image.
   *
   * `"illustration"` is the card and row's substitution; `"label"` is
   * `ItemImageBox`'s own arm, which the table reaches by passing an empty URL.
   */
  emptyState?: "illustration" | "label";
  /**
   * The item is still awaiting approval, so the `"illustration"` empty state
   * reads "Missing Image" instead of drawing the house.
   *
   * Ignored under `emptyState="label"`, where the donor's table draws the same
   * bordered text either way (`items-data-table.tsx:117-125`).
   */
  pendingApproval?: boolean;
  /**
   * Whose alt rule: the catalog card's (`item-card.tsx:203` — `Image of
   * {name}` on the hero *and* on the substituted illustration), or the Item
   * Search island card's, which differs (#964 N29): the hero announces the
   * bare name (`ItemCard.tsx:262`, `alt={item.name || ''}`), and the missing-
   * image box its own literal (`ItemEmptyImageBox.tsx:51`).
   */
  altStyle?: "catalog" | "island";
  className?: string;
}

/**
 * The card block's picture: the hero, its alt, and its two empty arms.
 *
 * The card and the list row draw the *same block* — `item-data-row.tsx:186-188`
 * is `item-card.tsx:202-204`, argument for argument — so the values it derives
 * from the item are named here once rather than spelled at both call sites.
 * That is not only tidiness: `pendingApproval` is deliberately absent from the
 * table's cell, so two hand-kept copies of the card's set are exactly where one
 * view would quietly stop agreeing with the other.
 *
 * The table keeps calling `ItemImage` directly, because its cell is a different
 * configuration of the donor's box rather than this block at another size.
 */
/**
 * `heightPx` + `slotSizes` come from the box's own union rather than being
 * re-declared here as two independently optional props.
 *
 * Full column width at this pixel height, rather than a square rung, passed
 * through to whichever box this call ends up drawing so the picture and its two
 * empty arms are all the same shape — the Add Variant dialog's preview hero is
 * the caller (`height: 250`). Inheriting the union rather than restating it is
 * what stops a caller of *this* component stating a height and forgetting the
 * slot width, which it could until #499's code review; `ItemImageBox`'s own
 * `ItemImageBoxSlot` carries why that mattered and why the value may not be a
 * percentage.
 */
type ItemImageProps = ItemImageBaseProps & ItemImageBoxSlot;

export function ItemHeroImage({ item }: { item: CatalogItemHeroInput }) {
  return (
    <ItemImage
      image={item.heroImage}
      itemName={item.name}
      size="medium"
      pendingApproval={!item.isApproved}
    />
  );
}

export function ItemImage({
  image,
  itemName,
  size = "small",
  bordered = false,
  emptyState = "illustration",
  pendingApproval = false,
  altStyle = "catalog",
  className,
  ...slot
}: ItemImageProps) {
  // The error flag, reset when the **bytes** this slot asks for change.
  //
  // The donor clears its flag in an effect keyed on the hero URL
  // (`ItemCard.tsx:249-252`), which is what makes a card retry after a failed
  // fetch instead of staying on the placeholder for good. `useItemImageFailure`
  // is that, and it carries the reasoning for both halves — why the key is the
  // picture rather than a memory of which URL failed, and why the reset lands
  // during render rather than in an effect.
  //
  // **Keyed on the whole source bundle, not on `originalUrl` alone** (#499
  // cross-review round 3). Keying on the original is a verdict that outlives
  // the fetch it describes: a legacy row whose AVIF original a browser cannot
  // decode fails here, then #500 stamps it and its renditions appear — and the
  // original URL has not moved, so the card stays on the placeholder until it
  // happens to remount. That is the exact failure `use-item-image-failure.ts`
  // was written to prevent, and this call site was not using it.
  const sources = itemImageSources(image);
  const [failed, noteFailed] = useItemImageFailure(sources);

  const island = altStyle === "island";
  const alt = island ? (itemName ?? "") : itemImageAlt(itemName);

  if (image && !failed) {
    return (
      <ItemImageBox
        src={sources.src}
        alt={alt}
        sources={sources.sources}
        size={size}
        {...slot}
        bordered={bordered}
        className={className}
        onError={noteFailed}
      />
    );
  }

  // The table's arm: the box's own no-URL state, reached by handing it no URL.
  if (emptyState === "label") {
    return (
      <ItemImageBox alt={alt} size={size} {...slot} bordered={bordered} className={className} />
    );
  }

  return (
    <ItemEmptyImageBox
      isPendingApproval={pendingApproval}
      // The donor's oddity, kept: the illustration travels as the box's own
      // `imgUrl`, so a named item's *placeholder* still announces the item's
      // name (`item-card.tsx:202-203`). Not tidied up here — it is what a modsy
      // screen-reader user hears. The island's card mounts the box itself, so
      // it carries the box's own literal.
      alt={island ? ITEM_PLACEHOLDER_ALT : alt}
      size={size}
      // The empty box has no picture, so it takes the height alone — its own
      // `heightPx` is independent of the slot width the union pairs.
      heightPx={slot.heightPx}
      className={className}
    />
  );
}
